import { useState } from 'react';
import { Link } from 'react-router-dom';

const NEWSLETTER_URL = 'https://readdy.ai/api/form/d75697r41deorl02f5h0';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      const body = new URLSearchParams({ email });
      await fetch(NEWSLETTER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      setSubmitted(true);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          {/* Brand Column */}
          <div className="col-span-1">
            <div className="flex items-center mb-6">
              <img 
                src="https://static.readdy.ai/image/1c1efe20ab982279d0e71dac7f6c0a80/0bce3b64772fbbd5c34a483b40aa78a9.png" 
                alt="Swasthya Logo" 
                className="h-10 w-auto"
              />
              <span className="ml-3 text-xl font-serif font-bold text-sand-100">Swasthya</span>
            </div>
            <div className="space-y-2 text-gray-400 text-sm leading-relaxed">
              <p>Ayurvedic wellness retreat in Fuerteventura</p>
              <p>Ancient wisdom meets modern healing</p>
              <p>Transform your journey within</p>
            </div>
          </div>

          {/* Newsletter Column */}
          <div className="col-span-1">
            <h3 className="text-sm uppercase tracking-wider text-sand-100 mb-4 font-semibold">Stay Connected</h3>
            {submitted ? (
              <p className="text-sage-400 text-sm py-2">Thank you for subscribing!</p>
            ) : (
              <form onSubmit={handleNewsletterSubmit} data-readdy-form className="mb-4">
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    className="w-full bg-transparent border-b border-sage-500 pb-2 text-white placeholder-gray-400 focus:outline-none focus:border-sage-300 transition-colors text-sm"
                    required
                  />
                  <button
                    type="submit"
                    className="absolute right-0 bottom-2 text-sage-400 hover:text-sage-300 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-lg"></i>
                  </button>
                </div>
              </form>
            )}
            <p className="text-xs text-gray-500">
              Join our wellness community •{' '}
              <Link to="/privacy-policy" className="text-sage-400 hover:text-sage-300 cursor-pointer">Privacy</Link>
            </p>
          </div>

          {/* Quick Links Column */}
          <div className="col-span-1">
            <h3 className="text-sm uppercase tracking-wider text-sand-100 mb-4 font-semibold">Explore</h3>
            <div className="space-y-3">
              {[
                { name: 'About Ayurveda', id: 'about' },
                { name: 'Retreat Schedule', id: 'schedule' },
                { name: 'Testimonials', id: 'experiences' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block text-sand-100 hover:text-sage-300 transition-colors text-sm cursor-pointer"
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          {/* Connect Column */}
          <div className="col-span-1">
            <h3 className="text-sm uppercase tracking-wider text-sand-100 mb-4 font-semibold">Connect</h3>
            <div className="space-y-3">
              <a href="tel:+34643939427" className="flex items-center text-sand-100 hover:text-sage-300 transition-colors text-sm cursor-pointer font-semibold">
                <i className="ri-phone-fill mr-2 text-base"></i>
                +34 643 939 427
              </a>
              <a href="mailto:swasthya.retreats@gmail.com" className="block text-sand-100 hover:text-sage-300 transition-colors text-sm cursor-pointer">Email</a>
              <a href="https://www.instagram.com/swasthya.retreats/" target="_blank" rel="noopener noreferrer" className="block text-sand-100 hover:text-sage-300 transition-colors text-sm cursor-pointer">Instagram</a>
              <a href="https://www.facebook.com/swasthyaretreats/" target="_blank" rel="noopener noreferrer" className="block text-sand-100 hover:text-sage-300 transition-colors text-sm cursor-pointer">Facebook</a>
              <a href="https://wa.me/32643939427" target="_blank" rel="noopener noreferrer" className="block text-sand-100 hover:text-sage-300 transition-colors text-sm cursor-pointer">WhatsApp</a>
            </div>
          </div>
        </div>

        {/* Large Brand Signature */}
        <div className="text-center mb-8">
          <h2 className="text-8xl md:text-9xl font-serif font-bold text-transparent stroke-2 stroke-sand-200 select-none tracking-wider">
            SWASTHYA
          </h2>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
          <p>&copy; 2024 Swasthya Retreat. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy-policy" className="hover:text-sage-300 transition-colors cursor-pointer">Privacy Policy</Link>
            <a href="#" className="hover:text-sage-300 transition-colors cursor-pointer">Terms of Service</a>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-sage-300 transition-colors cursor-pointer">Website Builder</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;